import { useEffect, useMemo, useRef, useState } from 'react';

export type CalendarAppointment = {
  appointmentId:string; customerId:string; customerName:string; status:string;
  serviceCode:string; serviceName:string; resourceId:string; resourceName:string;
  startsAt:string; serviceEndsAt:string; occupancyEndsAt:string;
  // The server must explicitly supply these before credit-dependent colors can be used.
  hasServiceCredit?:boolean|null; openSale?:boolean|null; multipleUpcoming?:boolean|null;
  checkoutSettled?:boolean|null; returnedServiceCredit?:boolean|null;
  multipleCheckedOut?:boolean|null;
  isEmployee?:boolean;isMember?:boolean;birthdayMonth?:number|null;birthdayDay?:number|null;
  importantNoteCount?:number;reservationNoteCount?:number;
  futureDiscount?:PendingSaleDiscount|null;
};
export type CalendarBlock = {
  blockId:string; resourceId:string; resourceName:string; reason:string;
  category:string; startsAt:string; endsAt:string;
};
export type CalendarGuestHold = {
  holdId:string;resourceId:string;resourceName:string;customerName:string;
  serviceName:string;startsAt:string;endsAt:string;expiresAt:string;
};
export type CalendarResource = {resourceId:string;name:string};
export type CalendarConfiguredStart = {resourceId:string;startMinute:number;source?:string};
type Profile = {displayName:string;email:string|null;phoneE164:string|null};
type Visits = {appointments:{startsAt:string;serviceName:string;locationName:string;locationTimezone?:string}[];hasMore:boolean};
type Preview = {state:'loading'|'ready'|'denied'|'error';profile?:Profile;visits?:Visits};
type StaffNote={
  noteId:string;text:string;important:boolean;version:number;
  createdAt:string;authorName:string;
};
type AppointmentNotes={
  state:'loading'|'ready'|'error';reservation:StaffNote[];important:StaffNote[];
};
export type PendingSaleDiscount={id:string;code:string;title:string};
type DiscountChoice={id:number;title:string;code:string;description:string;channel:string};

type Props = {
  day:string; timeZone:string; locationCode:string;
  appointments:CalendarAppointment[]; blocks:CalendarBlock[];
  guestHolds:CalendarGuestHold[]; resources:CalendarResource[];
  configuredStarts:CalendarConfiguredStart[];
  filterServices?:Array<{serviceId:string;name:string;resourceIds:string[]}>;
  configuredStartState:'loading'|'ready'|'signin'|'denied'|'error';
  bookingDataReady:boolean;
  writesEnabled:boolean; onDayChange:(day:string)=>void; onRefresh:()=>void;
  onGoPublic:()=>void;onOpenCancellations?:(day:string)=>void;onOpenWaitlist?:()=>void;
  onOpenRoom?:(resourceId:string)=>void;
  onOpenSlot?:(resourceId:string,startMinute:number,rect:DOMRect)=>void;
  resourceState:'loading'|'ready'|'signin'|'denied'|'error';
  checkInEnabled:boolean;
  checkoutBusy:boolean;
  onCheckout:(item:CalendarAppointment,forceNewSale?:boolean,
    discount?:PendingSaleDiscount|null)=>Promise<'checked_out'|'sale_required'|void>;
  onCancelWithoutCharge:(item:CalendarAppointment)=>void;
  onCancelWithCredit:(item:CalendarAppointment)=>void;
  onReschedule:(item:CalendarAppointment)=>void;
  onCheckoutOtherCustomer:(item:CalendarAppointment,discount?:PendingSaleDiscount|null)=>void;
  onCheckIn:(item:CalendarAppointment)=>void;
  onComplete:(item:CalendarAppointment)=>void;
  onOpenCustomer?:(item:CalendarAppointment)=>void;
  onReleaseGuestHold?:(holdId:string)=>void;
  onReleaseBlock?:(blockId:string)=>void;
  onReleaseBlocks?:(blockIds:string[])=>void;
  onSelectBlockRange?:(resourceIds:string[],startMinute:number,endMinute:number)=>void;
  blockWriteEnabled:boolean;
};

const minuteFormatters=new Map<string,Intl.DateTimeFormat>();
const dayFormatters=new Map<string,Intl.DateTimeFormat>();
function wallMinute(instant:string,zone:string):number {
  let formatter=minuteFormatters.get(zone);
  if(!formatter){
    formatter=new Intl.DateTimeFormat('en-US',{timeZone:zone,hour:'2-digit',
      minute:'2-digit',hourCycle:'h23'});
    minuteFormatters.set(zone,formatter);
  }
  const parts=formatter.formatToParts(new Date(instant));
  const part=(key:string)=>Number(parts.find(value=>value.type===key)?.value??'0');
  return part('hour')*60+part('minute');
}
function wallDay(instant:string,zone:string) {
  let formatter=dayFormatters.get(zone);
  if(!formatter){
    formatter=new Intl.DateTimeFormat('en-CA',{timeZone:zone,year:'numeric',
      month:'2-digit',day:'2-digit'});
    dayFormatters.set(zone,formatter);
  }
  const parts=formatter.formatToParts(new Date(instant));
  const part=(key:string)=>parts.find(value=>value.type===key)?.value??'';
  return part('year')+'-'+part('month')+'-'+part('day');
}
function hourLabel(hour:number) {
  return new Intl.DateTimeFormat('en-US',{hour:'numeric',timeZone:'UTC'})
    .format(new Date(Date.UTC(2030,0,1,hour)));
}
function startMinuteLabel(startMinute:number){
  const hour=Math.floor(startMinute/60),minute=startMinute%60;
  return (hour%12||12)+':'+String(minute).padStart(2,'0')+(hour<12?'am':'pm');
}
function formattedDate(day:string) {
  return new Intl.DateTimeFormat('en-US',{weekday:'short',month:'short',day:'numeric'})
    .format(new Date(day+'T12:00:00Z'));
}
export function relativeScheduleDate(day:string,zone:string,now=new Date().toISOString()):string {
  const today=wallDay(now,zone);
  const days=Math.round((Date.parse(day+'T12:00:00Z')-
    Date.parse(today+'T12:00:00Z'))/86400000);
  if(days===0)return 'Today';
  if(days===1)return 'Tomorrow';
  if(days===-1)return 'Yesterday';
  return Math.abs(days)+' days '+(days>0?'from now':'ago');
}
function moveDay(day:string,offset:number) {
  const base=Date.parse(day+'T12:00:00Z');
  return new Date(base+offset*86400000).toISOString().slice(0,10);
}
function todayAt(zone:string) {return wallDay(new Date().toISOString(),zone);}
export function birthdayNearNow(item:CalendarAppointment,zone:string):boolean {
  if(!Number.isInteger(item.birthdayMonth)||!Number.isInteger(item.birthdayDay))return false;
  const month=item.birthdayMonth!,day=item.birthdayDay!;
  if(month<1||month>12||day<1||day>31)return false;
  const today=todayAt(zone);
  const year=Number(today.slice(0,4)),m=Number(today.slice(5,7)),d=Number(today.slice(8,10));
  const current=Date.UTC(year,m-1,d,12);
  return [year-1,year,year+1].some(y=>{
    const birthday=Date.UTC(y,month-1,day,12);
    return new Date(birthday).getUTCMonth()===month-1&&
      Math.abs(birthday-current)<=45*86400000;
  });
}
function timeLabel(instant:string,zone:string) {
  return new Intl.DateTimeFormat('en-US',{timeZone:zone,hour:'numeric',minute:'2-digit'})
    .format(new Date(instant));
}
function colorFor(item:CalendarAppointment) {
  if(item.status==='NO_SHOW')return 'no-show';
  if(item.checkoutSettled===true){
    if(item.returnedServiceCredit===true)return 'returned-credit';
    if(item.multipleCheckedOut===true)return 'multiple-checked-out';
    return 'completed';
  }
  if(item.status==='COMPLETED')return 'completed-unverified';
  if(item.status==='CANCELLED')return 'cancelled';
  if(item.openSale===true)return 'open-sale';
  if(item.hasServiceCredit===true)return 'future-credit';
  if(item.hasServiceCredit===false)return 'future-unpaid';
  return 'unverified-credit';
}

type PopupPlacement={left:number;top:number;maxHeight:number};
function positionBy(rect:DOMRect,width:number):PopupPlacement {
  const vw=window.innerWidth,vh=window.innerHeight;
  const left=rect.right+10+width<=vw-10 ? rect.right+10
    :rect.left-width-10>=10 ? rect.left-width-10
    :Math.max(10,vw-width-10);
  const top=Math.max(48,Math.min(rect.top-8,vh-220));
  return {left,top,maxHeight:Math.max(160,Math.min(610,vh-top-12))};
}

export function StaffCalendar({
  day,timeZone,locationCode,appointments,blocks,guestHolds,resources,
  configuredStarts,filterServices=[],configuredStartState,bookingDataReady,writesEnabled,onDayChange,
  onRefresh,onGoPublic,onOpenCancellations,onOpenWaitlist,onOpenRoom,onOpenSlot,resourceState,
  checkInEnabled,checkoutBusy,onCheckout,onCancelWithoutCharge,onCancelWithCredit,onReschedule,
  onCheckoutOtherCustomer,onCheckIn,onComplete,onOpenCustomer,onReleaseGuestHold,
  onReleaseBlock,onReleaseBlocks,onSelectBlockRange,blockWriteEnabled,
}:Props) {
  const [legendOpen,setLegendOpen]=useState(false);
  const [blockMode,setBlockMode]=useState(false);
  const [unblockMode,setUnblockMode]=useState(false);
  const [draw,setDraw]=useState<{fromRoom:number;toRoom:number;
    fromMinute:number;toMinute:number}|null>(null);
  const drawRef=useRef<{fromRoom:number;toRoom:number;
    fromMinute:number;toMinute:number}|null>(null);
  const [advanced,setAdvanced]=useState(false);
  const [compact,setCompact]=useState(false);
  const [filterOpen,setFilterOpen]=useState(false);
  const [filterSelection,setFilterSelection]=useState('');
  const [appliedService,setAppliedService]=useState('');
  const [datePickerOpen,setDatePickerOpen]=useState(false);
  const dateInput=useRef<HTMLInputElement|null>(null);
  const [selectedId,setSelectedId]=useState<string|null>(null);
  const [selectedGuestHold,setSelectedGuestHold]=useState<string|null>(null);
  const [hoverId,setHoverId]=useState<string|null>(null);
  const [appointmentPlacement,setAppointmentPlacement]=useState<PopupPlacement|null>(null);
  const [customerPlacement,setCustomerPlacement]=useState<PopupPlacement|null>(null);
  const hoverTimer=useRef<ReturnType<typeof setTimeout>|null>(null);
  const [preview,setPreview]=useState<Preview|null>(null);
  const [appointmentNotes,setAppointmentNotes]=useState<AppointmentNotes|null>(null);
  const [reservationNoteText,setReservationNoteText]=useState('');
  const [reservationNoteImportant,setReservationNoteImportant]=useState(false);
  const [reservationNoteBusy,setReservationNoteBusy]=useState(false);
  const [reservationNoteStatus,setReservationNoteStatus]=useState('');
  const [reservationNotesRefresh,setReservationNotesRefresh]=useState(0);
  const [pendingDiscount,setPendingDiscount]=useState<PendingSaleDiscount|null>(null);
  const [futureDiscountBusy,setFutureDiscountBusy]=useState(false);
  const [futureDiscountError,setFutureDiscountError]=useState('');
  const [futureDiscountSearchOpen,setFutureDiscountSearchOpen]=useState(false);
  const [futureDiscountQuery,setFutureDiscountQuery]=useState('');
  const [futureDiscountChoices,setFutureDiscountChoices]=useState<DiscountChoice[]>([]);

  const selected=appointments.find(item=>item.appointmentId===selectedId)??null;
  const hovered=appointments.find(item=>item.appointmentId===hoverId)??null;

  useEffect(()=>{
    setSelectedId(null);setSelectedGuestHold(null);setHoverId(null);
    setAppointmentPlacement(null);setCustomerPlacement(null);
  },[day,locationCode]);
  useEffect(()=>()=>{if(hoverTimer.current)clearTimeout(hoverTimer.current);},[]);

  const showAppointment=(item:CalendarAppointment,rect:DOMRect)=>{
    if(hoverTimer.current)clearTimeout(hoverTimer.current);
    setHoverId(null);
    setCustomerPlacement(null);
    setAdvanced(false);
    setAppointmentPlacement(positionBy(rect,340));
    setSelectedId(item.appointmentId);
  };
  const showCustomer=(item:CalendarAppointment,rect:DOMRect,delay=0)=>{
    if(hoverTimer.current)clearTimeout(hoverTimer.current);
    const perform=()=>{
      setSelectedId(null);setAppointmentPlacement(null);
      setCustomerPlacement(positionBy(rect,460));
      setHoverId(item.appointmentId);
    };
    if(delay>0)hoverTimer.current=setTimeout(perform,delay);
    else perform();
  };
  const cancelPendingHover=()=>{
    if(hoverTimer.current)clearTimeout(hoverTimer.current);
    hoverTimer.current=null;
  };
  const closePopups=()=>{
    cancelPendingHover();setAdvanced(false);setSelectedId(null);setHoverId(null);
    setAppointmentPlacement(null);setCustomerPlacement(null);
  };
  useEffect(()=>{
    if(!hovered){setPreview(null);return;}
    const controller=new AbortController();
    const customerId=encodeURIComponent(hovered.customerId);
    const q='?'+new URLSearchParams({location:locationCode});
    setPreview({state:'loading'});
    void Promise.all([
      fetch('/api/v1/staff/customers/'+customerId+q,{
        signal:controller.signal,credentials:'same-origin',cache:'no-store',
      }),
      fetch('/api/v1/staff/customers/'+customerId+'/appointments'+q,{
        signal:controller.signal,credentials:'same-origin',cache:'no-store',
      }),
    ]).then(async([detail,history])=>{
      if(controller.signal.aborted)return;
      if(detail.status===401||detail.status===403){
        setPreview({state:'denied'});return;
      }
      if(!detail.ok)throw new Error('Profile unavailable');
      const body=await detail.json() as {ok:boolean;data?:{profile:Profile}};
      if(!body.ok||!body.data)throw new Error('Profile unavailable');
      let visits:Visits|undefined;
      if(history.ok){
        const result=await history.json() as {ok:boolean;data?:Visits};
        if(result.ok)visits=result.data;
      }
      if(!controller.signal.aborted)setPreview({state:'ready',profile:body.data.profile,visits});
    }).catch(()=>{if(!controller.signal.aborted)setPreview({state:'error'});});
    return ()=>controller.abort();
  },[hovered?.customerId,locationCode]);

  useEffect(()=>{
    if(!selected){setAppointmentNotes(null);return;}
    const controller=new AbortController();
    setAppointmentNotes({state:'loading',reservation:[],important:[]});
    const q='?'+new URLSearchParams({location:locationCode});
    const get=async(kind:'appointment'|'customer',targetId:string)=>{
      const res=await fetch('/api/v1/staff/notes/'+kind+'/'+
        encodeURIComponent(targetId)+q,{
        signal:controller.signal,credentials:'same-origin',cache:'no-store',
      });
      if(!res.ok)throw Error('Notes unavailable');
      const body=await res.json() as {ok:boolean;data?:{notes:StaffNote[]}};
      if(!body.ok||!Array.isArray(body.data?.notes))throw Error('Notes unavailable');
      return body.data.notes;
    };
    void Promise.allSettled([
      get('appointment',selected.appointmentId),
      get('customer',selected.customerId),
    ]).then(([reservation,customer])=>{
      if(controller.signal.aborted)return;
      setAppointmentNotes({
        state:reservation.status==='fulfilled'?'ready':'error',
        reservation:reservation.status==='fulfilled'?reservation.value:[],
        important:customer.status==='fulfilled'
          ?customer.value.filter(note=>note.important):[],
      });
    });
    return ()=>controller.abort();
  },[selectedId,selected?.customerId,locationCode,reservationNotesRefresh]);
  const addReservationNote=async()=>{
    if(!selected||!reservationNoteText.trim()||reservationNoteBusy)return;
    setReservationNoteBusy(true);setReservationNoteStatus('');
    try{
      const res=await fetch('/api/v1/staff/notes/appointment/'+
        encodeURIComponent(selected.appointmentId),{
        method:'POST',credentials:'same-origin',headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,text:reservationNoteText.trim(),
          important:reservationNoteImportant,idempotencyKey:crypto.randomUUID()}),
      });
      const body=await res.json() as {ok:boolean;error?:{code:string}};
      if(!res.ok||!body.ok)throw Error(body.error?.code??'NOTE_SAVE_FAILED');
      setReservationNoteText('');setReservationNoteImportant(false);
      setReservationNotesRefresh(n=>n+1);
      onRefresh();
    }catch(error){setReservationNoteStatus(error instanceof Error?error.message:'Reservation note unavailable.');}
    finally{setReservationNoteBusy(false);}
  };
  const modifyReservationNote=async(noteId:string,expectedVersion:number,important?:boolean)=>{
    if(!selected||reservationNoteBusy)return;
    setReservationNoteBusy(true);setReservationNoteStatus('');
    try{
      const res=await fetch('/api/v1/staff/notes/appointment/'+
        encodeURIComponent(selected.appointmentId)+'/'+encodeURIComponent(noteId),{
        method:'PATCH',credentials:'same-origin',headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,expectedVersion,
          ...(important===undefined?{delete:true}:{important})}),
      });
      const body=await res.json() as {ok:boolean;error?:{code:string}};
      if(!res.ok||!body.ok)throw Error(body.error?.code??'NOTE_UPDATE_FAILED');
      setReservationNotesRefresh(n=>n+1);onRefresh();
    }catch(error){setReservationNoteStatus(error instanceof Error?error.message:'Reservation note unavailable.');}
    finally{setReservationNoteBusy(false);}
  };

  useEffect(()=>{
    setPendingDiscount(selected?.futureDiscount??null);
    setFutureDiscountSearchOpen(false);setFutureDiscountQuery('');
    setFutureDiscountChoices([]);setFutureDiscountError('');
  },[selectedId,locationCode,selected?.futureDiscount?.id]);
  const saveFutureDiscount=async(discount:PendingSaleDiscount|null)=>{
    if(!selected||futureDiscountBusy||!writesEnabled)return;
    setFutureDiscountBusy(true);setFutureDiscountError('');
    try{
      const response=await fetch('/api/v1/staff/bookings/'+
        encodeURIComponent(selected.appointmentId)+'/future-discount',{
        method:'PUT',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,discountId:discount?.id??null}),
      });
      const body=await response.json() as {ok:boolean;data?:{discount:PendingSaleDiscount|null};
        error?:{code?:string;message?:string}};
      if(!response.ok||!body.ok||!body.data)
        throw Error(body.error?.message??body.error?.code??'Unable to save future discount.');
      setPendingDiscount(body.data.discount);
      setFutureDiscountSearchOpen(false);
      onRefresh();
    }catch(error){
      setFutureDiscountError(error instanceof Error?error.message:'Could not save future discount.');
    }finally{setFutureDiscountBusy(false);}
  };
  useEffect(()=>{
    if(!selected||!futureDiscountSearchOpen)return;
    const controller=new AbortController();
    const timer=window.setTimeout(()=>{
      void fetch('/api/v1/staff/discounts?'+new URLSearchParams({
        location:locationCode,q:futureDiscountQuery,
      }),{signal:controller.signal,credentials:'same-origin',cache:'no-store'})
        .then(async res=>{
          if(!res.ok)throw Error('Discount search unavailable');
          const body=await res.json() as {ok:boolean;data?:{discounts:DiscountChoice[]}};
          if(body.ok&&Array.isArray(body.data?.discounts)&&!controller.signal.aborted)
            setFutureDiscountChoices(body.data.discounts);
        }).catch(()=>{
          if(!controller.signal.aborted)setFutureDiscountChoices([]);
        });
    },180);
    return ()=>{window.clearTimeout(timer);controller.abort();};
  },[selectedId,futureDiscountSearchOpen,futureDiscountQuery,locationCode]);
  const ordered=useMemo(()=>{
    const known=new Map<string,CalendarResource>();
    for(const room of resources)known.set(room.resourceId,room);
    for(const entry of appointments){
      if(!known.has(entry.resourceId))known.set(entry.resourceId,{
        resourceId:entry.resourceId,name:entry.resourceName,
      });
    }
    for(const block of blocks){
      if(!known.has(block.resourceId))known.set(block.resourceId,{
        resourceId:block.resourceId,name:block.resourceName,
      });
    }
    // The server supplies the same persisted order used by Rooms.
    // Newly referenced appointments are appended without reordering known rooms.
    const rooms=Array.from(known.values());
    const selected=filterServices.find(service=>service.serviceId===appliedService);
    return selected?rooms.filter(room=>selected.resourceIds.includes(room.resourceId)):rooms;
  },[resources,appointments,blocks,filterServices,appliedService]);

  // Index recurring starts once per date. FloatHelm's recovered schedules may
  // contain over 1,000 room starts; repeated filtering/sorting for every slot
  // can stall the browser and detach interactive staff navigation.
  const startsByRoom=useMemo(()=>{
    const grouped=new Map<string,CalendarConfiguredStart[]>();
    for(const start of configuredStarts){
      const current=grouped.get(start.resourceId);
      if(current)current.push(start);
      else grouped.set(start.resourceId,[start]);
    }
    for(const starts of grouped.values())starts.sort((a,b)=>a.startMinute-b.startMinute);
    return grouped;
  },[configuredStarts]);
  // Every lane's next configured boundary can be computed once instead of
  // linearly searching hundreds of starts for every rendered white slot.
  const nextStartByRoom=useMemo(()=>{
    const grouped=new Map<string,Map<number,number|undefined>>();
    for(const [id,starts] of startsByRoom){
      const next=new Map<number,number|undefined>();
      for(let i=0;i<starts.length;i++)
        next.set(starts[i].startMinute,starts[i+1]?.startMinute);
      grouped.set(id,next);
    }
    return grouped;
  },[startsByRoom]);

  // These hours are VIEWPORT boundaries, not operational hours or bookable slots.
  const range=useMemo(()=>{
    const moments=[...appointments.map(item=>wallMinute(item.startsAt,timeZone)),
      ...appointments.map(item=>wallMinute(item.occupancyEndsAt,timeZone)),
      ...blocks.map(item=>wallMinute(item.startsAt,timeZone)),
      ...guestHolds.map(item=>wallMinute(item.startsAt,timeZone)),
      ...guestHolds.map(item=>wallMinute(item.endsAt,timeZone)),
      ...blocks.map(item=>wallMinute(item.endsAt,timeZone))];
    const earliest=Math.min(...moments,...configuredStarts.map(s=>s.startMinute),600);
    return {start:Math.max(0,Math.floor(earliest/60)),
      end:Math.min(24,Math.max(23,Math.ceil(Math.max(...moments,1380)/60)))};
  },[appointments,blocks,guestHolds,configuredStarts,timeZone]);
  const hourPx=65;
  const fullHeight=(range.end-range.start)*hourPx;
  const topFor=(instant:string)=>Math.max(0,
    (wallMinute(instant,timeZone)-range.start*60)/60*hourPx);
  const heightFor=(start:string,end:string)=>Math.max(19,
    (wallMinute(end,timeZone)-wallMinute(start,timeZone))/60*hourPx);
  const snap15=(value:number)=>Math.max(0,Math.min(1425,Math.floor(value/15)*15));
  const pointOnCalendar=(clientX:number,clientY:number)=>{
    const element=document.elementFromPoint(clientX,clientY);
    const lane=element?.closest<HTMLElement>('.calendar-room-lane');
    if(!lane)return null;
    const index=Number(lane.dataset.roomIndex);
    if(!Number.isInteger(index)||index<0||index>=ordered.length)return null;
    const rect=lane.getBoundingClientRect();
    const minute=snap15(range.start*60+(clientY-rect.top)/hourPx*60);
    return {index,minute};
  };
  const visible=appointments.filter(item=>item.status!=='CANCELLED'
    &&wallDay(item.startsAt,timeZone)===day);

  return <section className="staff-calendar" aria-label="Staff appointment calendar">
    <div className="calendar-toolbar">
      <div className="calendar-toolbar-links">
        <button type="button" onClick={()=>onDayChange(todayAt(timeZone))}>Today</button>
        <button type="button" onClick={onGoPublic}>Public</button>
        <button type="button" title="View cancellations for this day." onClick={()=>onOpenCancellations?.(day)} disabled={!onOpenCancellations}>Cancellations</button>
      </div>
      <div className="calendar-date-navigation">
        <button type="button" aria-label="Previous week" onClick={()=>onDayChange(moveDay(day,-7))}>← Week</button>
        <button type="button" aria-label="Previous day" onClick={()=>onDayChange(moveDay(day,-1))}>← Day</button>
        <span className="calendar-date-anchor">
            <button type="button" aria-label="Open calendar picker" className="calendar-date-trigger"
              aria-expanded={datePickerOpen}
              onClick={()=>setDatePickerOpen(value=>!value)}>
              {formattedDate(day)}
            </button>
            <small className="calendar-date-relative" aria-label="Day offset indicator">
              {relativeScheduleDate(day,timeZone)}
            </small>
            <input ref={dateInput} type="date" aria-label="Schedule date" className="calendar-date-picker"
              hidden={!datePickerOpen} value={day}
              onChange={event=>{onDayChange(event.target.value);setDatePickerOpen(false);}}/>
          </span>
        <button type="button" aria-label="Next day" onClick={()=>onDayChange(moveDay(day,1))}>Day →</button>
        <button type="button" aria-label="Next week" onClick={()=>onDayChange(moveDay(day,7))}>Week →</button>
      </div>
      <div className="calendar-tools">
        <button type="button" className="calendar-help-trigger"
          aria-label="Appointment color help" aria-expanded={legendOpen}
          aria-controls="ecfs-calendar-colors"
          onClick={()=>setLegendOpen(open=>!open)}>?</button>
        <button type="button" aria-expanded={filterOpen}
          onClick={()=>{setFilterSelection(appliedService);setFilterOpen(open=>!open);}}>Filter</button>
        <button type="button" onClick={onOpenWaitlist} disabled={!onOpenWaitlist} title="View waitlist for this day">Waitlist</button>
        <button type="button" onClick={onRefresh}>Refresh</button>
      </div>
    </div>
    {filterOpen&&<div className="calendar-filter-backdrop" onClick={()=>setFilterOpen(false)}>
      <form role="dialog" aria-label="Filter Schedule" className="calendar-filter-dialog"
        onClick={event=>event.stopPropagation()}
        onSubmit={event=>{event.preventDefault();setAppliedService(filterSelection);
          setFilterOpen(false);}}>
        <h3>Filter Schedule</h3>
        <label>Filter by Service
          <select aria-label="Service Type" value={filterSelection}
            onChange={event=>setFilterSelection(event.target.value)}>
            <option value="">All Services</option>
            {filterServices.map(service=><option key={service.serviceId}
              value={service.serviceId}>{service.name}</option>)}
          </select>
        </label>
        <div className="calendar-filter-actions">
          <button type="submit">Apply</button>
          <button type="button" onClick={()=>setFilterOpen(false)}>Cancel</button>
        </div>
      </form>
    </div>}
    <div className="calendar-width-tools">
      <button type="button" className="calendar-block-mode"
        aria-pressed={blockMode} disabled={!blockWriteEnabled}
        onClick={()=>{setBlockMode(current=>!current);setUnblockMode(false);
          setDraw(null);drawRef.current=null;}}>
        {blockMode?'Finish Blocking':'Block Out'}</button>
      <button type="button" className="calendar-block-mode calendar-unblock-mode"
        aria-pressed={unblockMode} disabled={!blockWriteEnabled}
        onClick={()=>{setUnblockMode(current=>!current);setBlockMode(false);
          setDraw(null);drawRef.current=null;}}>
        {unblockMode?'Finish Unblocking':'Unblock'}</button>
      {(blockMode||unblockMode)&&<span>Drag across times and rooms to
        {' '+(unblockMode?'remove existing blockouts.':'block out.')}</span>}
      <button type="button" className="calendar-width-toggle" aria-pressed={compact}
        onClick={()=>setCompact(value=>!value)}>Toggle Schedule Width</button>
    </div>
    {configuredStartState==='error'&&
      <p role="alert">Configured start times could not be loaded.</p>}
    {ordered.length===0
      ?<div className="calendar-no-rooms" role="status">
        {resourceState==='loading'?'Loading authorized calendar resources…':
          resourceState==='signin'?'Sign in to view this location’s Calendar.':
          resourceState==='denied'?'You do not have permission to view this location’s Calendar.':
          resourceState==='error'?'Calendar room information is temporarily unavailable.':
          'No rooms/resources are configured for this location in staging.'}
      </div>
      :<div className="calendar-scroll" tabIndex={0} aria-label="Scrollable room calendar">
        <div className={'calendar-grid'+(compact?' is-compact':'')}
          onPointerDown={event=>{
            if((!blockMode&&!unblockMode)||event.button!==0)return;
            const point=pointOnCalendar(event.clientX,event.clientY);
            if(!point)return;
            event.preventDefault();
            const next={fromRoom:point.index,toRoom:point.index,
              fromMinute:point.minute,toMinute:point.minute};
            drawRef.current=next;setDraw(next);
          }}
          onPointerMove={event=>{
            if(!drawRef.current)return;
            const point=pointOnCalendar(event.clientX,event.clientY);
            if(!point)return;
            const next={...drawRef.current,toRoom:point.index,toMinute:point.minute};
            drawRef.current=next;setDraw(next);
          }}
          onPointerUp={event=>{
            const current=drawRef.current;if(!current)return;
            const point=pointOnCalendar(event.clientX,event.clientY);
            const next=point?{...current,toRoom:point.index,toMinute:point.minute}:current;
            drawRef.current=null;setDraw(null);
            const a=Math.min(next.fromRoom,next.toRoom);
            const b=Math.max(next.fromRoom,next.toRoom);
            const start=Math.min(next.fromMinute,next.toMinute);
            const end=Math.max(next.fromMinute,next.toMinute)+15;
            const resourceIds=ordered.slice(a,b+1).map(room=>room.resourceId);
            if(unblockMode){
              const selected=new Set(resourceIds);
              const ids=blocks.filter(block=>selected.has(block.resourceId)&&
                wallMinute(block.startsAt,timeZone)<end&&
                wallMinute(block.endsAt,timeZone)>start).map(block=>block.blockId);
              if(ids.length)onReleaseBlocks?.(ids);
            }else onSelectBlockRange?.(resourceIds,start,end);
          }}
          onPointerCancel={()=>{drawRef.current=null;setDraw(null);}}
          style={{gridTemplateColumns:compact
            ?'52px repeat('+ordered.length+', minmax(96px,1fr)) 40px'
            :'64px repeat('+ordered.length+', minmax(142px,1fr)) 48px'}}>
          <div className="calendar-corner" style={{gridRow:1,gridColumn:1}}/>
          {ordered.map((room,index)=><button type="button" className="calendar-room-heading" key={room.resourceId}
            style={{gridRow:1,gridColumn:index+2}}
            onClick={()=>onOpenRoom?.(room.resourceId)}
            aria-label={'Open '+room.name+' Room Schedule'}>{room.name}</button>)}
          <div className="calendar-corner" style={{gridRow:1,gridColumn:ordered.length+2}}/>
          <div className="calendar-time-axis" style={{height:fullHeight,gridRow:2,gridColumn:1}}>
            {Array.from({length:range.end-range.start+1},(_,index)=>{
              const hour=range.start+index;
              return <span className="calendar-hour" key={hour} style={{top:index*hourPx}}>
                {hourLabel(hour)}
              </span>;
            })}
          </div>
          <div className="calendar-time-axis calendar-time-axis-right"
        style={{height:fullHeight,gridRow:2,gridColumn:ordered.length+2}}>
        {Array.from({length:range.end-range.start+1},(_,index)=>{
          const hour=range.start+index;
          return <span className="calendar-hour" key={hour}
            style={{top:index*hourPx}}>{hourLabel(hour)}</span>;
        })}
      </div>
      {ordered.map((room,index)=><div key={room.resourceId} className="calendar-room-lane"
            data-room-index={index}
            style={{height:fullHeight,gridRow:2,gridColumn:index+2,
              backgroundSize:'100% '+hourPx+'px'}}>
            {(blockMode||unblockMode)&&<div className="calendar-block-draw-surface"
              aria-hidden="true"/>}
            {(blockMode||unblockMode)&&draw&&index>=Math.min(draw.fromRoom,draw.toRoom)&&
              index<=Math.max(draw.fromRoom,draw.toRoom)&&
              <div className={'calendar-block-draw-preview'+(unblockMode?' is-unblocking':'')}
                aria-hidden="true"
                style={{top:(Math.min(draw.fromMinute,draw.toMinute)-range.start*60)/60*hourPx,
                  height:Math.max(16,(Math.abs(draw.fromMinute-draw.toMinute)+15)/60*hourPx)}}/>}
            {(startsByRoom.get(room.resourceId)??[])
              // Princeton's sauna times exist only in the unverified Sept backup.
              // Keep that evidence archived, but never present it as open slots.
              .filter(start=>!(locationCode==='PR'&&
                (room.name==='Infrared Sauna'||room.name==='Sauna Guest')&&
                start.source==='PROTOTYPE_V114_UNVERIFIED'))
              .filter(start=>!visible.some(appointment=>appointment.resourceId===room.resourceId
                &&wallMinute(appointment.startsAt,timeZone)<=start.startMinute
                &&wallMinute(appointment.occupancyEndsAt,timeZone)>start.startMinute))
              .filter(start=>!guestHolds.some(hold=>hold.resourceId===room.resourceId
                 &&wallMinute(hold.startsAt,timeZone)<=start.startMinute
                 &&wallMinute(hold.endsAt,timeZone)>start.startMinute))
               .filter(start=>!blocks.some(block=>block.resourceId===room.resourceId
                &&wallMinute(block.startsAt,timeZone)<=start.startMinute
                &&wallMinute(block.endsAt,timeZone)>start.startMinute))
              .map(start=>{
                const nextMinute=nextStartByRoom.get(room.resourceId)?.get(start.startMinute);
                const isFloat=/^Float Room\s+\d+$/i.test(room.name);
                const minutes=isFloat
                  ?Math.min(155,nextMinute!==undefined?nextMinute-start.startMinute:155)
                  :Math.min(30,nextMinute!==undefined?nextMinute-start.startMinute:30);
                const historical=start.source==='PROTOTYPE_V114_UNVERIFIED';
                const cannotBook=historical||!bookingDataReady;
                return <button type="button" key={start.startMinute}
                  className={'calendar-open-slot'+(cannotBook?' prototype-unverified':'')}
                  style={{top:(start.startMinute-range.start*60)/60*hourPx,
                    height:Math.max(14,minutes/60*hourPx-3)}}
                  title={historical?'Historical prototype schedule, not verified for booking':
                    !bookingDataReady?'Calendar availability has not been verified for booking':
                    'Book '+room.name+' at '+startMinuteLabel(start.startMinute)}
                  aria-label={room.name+' '+startMinuteLabel(start.startMinute)+
                    (historical?' historical schedule':
                      !bookingDataReady?' booking unavailable':' open booking slot')}
                  disabled={cannotBook}
                  onClick={cannotBook?undefined:event=>onOpenSlot?.(room.resourceId,start.startMinute,
                    event.currentTarget.getBoundingClientRect())}>
                  <span>{startMinuteLabel(start.startMinute)}</span>
                </button>;
              })}
            {blocks.filter(block=>block.resourceId===room.resourceId).map(block=>
              <button type="button" key={block.blockId} className="calendar-block"
                disabled={!blockWriteEnabled}
                aria-label={'Unblock '+room.name+' '+block.reason}
                onClick={()=>onReleaseBlock?.(block.blockId)}
                title={block.category+': '+block.reason}
                style={{top:topFor(block.startsAt),height:heightFor(block.startsAt,block.endsAt)}}>
                <span>{block.reason}</span>
              </button>)}
            {guestHolds.filter(hold=>hold.resourceId===room.resourceId&&
               wallDay(hold.startsAt,timeZone)===day).map(hold=><button
               type="button" key={hold.holdId}
               className="calendar-guest-hold"
               aria-label={'Booking in progress for '+hold.customerName}
               style={{top:topFor(hold.startsAt),
                 height:heightFor(hold.startsAt,hold.endsAt)}}
               onClick={()=>{setSelectedGuestHold(hold.holdId);setSelectedId(null);}}>
               <strong>{hold.customerName}</strong>
               <small>{hold.serviceName}</small>
             </button>)}
             {visible.filter(item=>item.resourceId===room.resourceId).map(item=>
              <div key={item.appointmentId}
                className={'calendar-appointment '+colorFor(item)+
                  (item.multipleUpcoming===true?' multiple':'')+
                  (item.isEmployee||item.isMember?' has-identity-flag':'')+
                  (item.isEmployee&&item.isMember?' two-identity-flags':'')}
                style={{top:topFor(item.startsAt),
                  height:heightFor(item.startsAt,item.occupancyEndsAt)}}>
                {(item.isEmployee===true||item.isMember===true)&&
                  <div className="ecfs-calendar-top-flags" aria-label="Customer indicators">
                    {item.isEmployee===true&&
                      <span className="ecfs-calendar-top-flag employee"
                        role="img" title="Employee" aria-label="Employee">E</span>}
                    {item.isMember===true&&
                      <span className="ecfs-calendar-top-flag member"
                        role="img" title="Active member" aria-label="Active member">M</span>}
                  </div>}
                <button type="button" className="calendar-appointment-action"
                  aria-label={'Open appointment for '+item.customerName}
                  onClick={event=>showAppointment(item,
                    event.currentTarget.closest('.calendar-appointment')!.getBoundingClientRect())}>
                  <strong className="calendar-guest-name" onMouseEnter={event=>
                    showCustomer(item,event.currentTarget.getBoundingClientRect(),170)}
                    onMouseLeave={cancelPendingHover}>
                    {item.customerName}
                  </strong>
                  {(item.importantNoteCount??0)>0&&
                    <span className="ecfs-calendar-identity-icon important"
                      title="Important customer notes" aria-label="Important customer notes">!</span>}
                  {(item.reservationNoteCount??0)>0&&
                    <span className="ecfs-calendar-identity-icon"
                      title={item.reservationNoteCount+' reservation notes'}
                      aria-label={item.reservationNoteCount+' reservation notes'}>🗒</span>}
                  {birthdayNearNow(item,timeZone)&&
                    <span className="ecfs-calendar-identity-icon"
                      title={'Birthday on '+item.birthdayMonth+'/'+item.birthdayDay}
                      aria-label={'Birthday on '+item.birthdayMonth+'/'+item.birthdayDay}>
                      🎂</span>}
                  <small>{item.serviceName}</small>
                </button>
                <button type="button" className="calendar-guest-peek"
                  aria-label={'Preview customer '+item.customerName}
                  onMouseEnter={event=>showCustomer(item,event.currentTarget.getBoundingClientRect(),170)}
                  onMouseLeave={cancelPendingHover}
                  onFocus={event=>showCustomer(item,event.currentTarget.getBoundingClientRect())}
                  onClick={event=>{
                    event.stopPropagation();
                    showCustomer(item,event.currentTarget.getBoundingClientRect());
                  }}>ⓘ</button>
              </div>)}
          </div>)}
          <div className="calendar-time-axis calendar-time-axis-right"
            style={{height:fullHeight,gridRow:2,gridColumn:ordered.length+2}}>
            {Array.from({length:range.end-range.start+1},(_,index)=>
              <span className="calendar-hour" key={index}
                style={{top:index*hourPx}}>{hourLabel(range.start+index)}</span>)}
          </div>
        </div>
      </div>}
    {legendOpen&&<aside className="calendar-color-help" id="ecfs-calendar-colors"
      role="dialog" aria-label="Appointment colors">
      <div className="calendar-color-help-heading">
        <strong>Appointment Colors</strong>
        <button type="button" aria-label="Close appointment colors"
          onClick={()=>setLegendOpen(false)}>×</button>
      </div>
      <div className="calendar-legend" aria-label="Appointment color legend">
        <span><i className="legend-available"/> Available slot (click to reserve or block)</span>
        <span><i className="legend-blocked"/> Blocked slot, unavailable for customer booking</span>
        <span><i className="legend-future"/> Future appointment without available service credit</span>
        <span><i className="legend-credit"/> Future appointment with available service credit</span>
        <span><i className="legend-multiple"/> Multiple upcoming appointments for same customer</span>
        <span><i className="legend-sale"/> Open sale for service credit to pay for appointment</span>
        <span><i className="legend-completed"/> Successfully checked out appointment</span>
        <span><i className="legend-multiple-checked"/> Multiple checked-out appointments at same time</span>
        <span><i className="legend-returned"/> Checked out, service credit subsequently returned</span>
        <span><i className="legend-noshow"/> No-show appointment</span>
        <div className="calendar-staging-legend-label">Additional staging-only states</div>
        <span><i className="legend-unverified"/> Future appointment, service credit status not yet verified</span>
        <span><i className="legend-completed-unverified"/> Attendance completed, checkout status not verified</span>
      </div>
    </aside>}
    {selectedGuestHold&&guestHolds.some(hold=>hold.holdId===selectedGuestHold)&&
       <aside className="calendar-popover calendar-appointment-popup" role="dialog"
         aria-label="Booking hold details">
         {guestHolds.filter(hold=>hold.holdId===selectedGuestHold).map(hold=><div key={hold.holdId}>
           <div className="calendar-popover-heading"><strong>{hold.customerName}</strong>
             <button type="button" aria-label="Close hold details"
               onClick={()=>setSelectedGuestHold(null)}>×</button></div>
           <div className="calendar-popover-content">
             <h3>Booking in progress</h3>
             <p>{hold.serviceName}, {hold.resourceName}</p>
             <p>Hold expires {timeLabel(hold.expiresAt,timeZone)}</p>
             <button type="button" disabled={!writesEnabled||!onReleaseGuestHold}
               onClick={()=>{onReleaseGuestHold?.(hold.holdId);setSelectedGuestHold(null);}}>
               Release temporary hold</button>
           </div>
         </div>)}
       </aside>}
     {selected&&<aside className="calendar-popover calendar-appointment-popup"
      style={appointmentPlacement??undefined} role="dialog"
      aria-label={'Appointment details for '+selected.customerName}>
      <div className="calendar-popover-heading"><strong>{selected.customerName}</strong>
        <button type="button" onClick={closePopups} aria-label="Close appointment details">×</button>
      </div>
      <div className="calendar-popover-content">
        <h3>{selected.serviceName}</h3>
        <p>{timeLabel(selected.startsAt,timeZone)} to {timeLabel(selected.serviceEndsAt,timeZone)}</p>
        <p>{selected.resourceName}</p>
        <p className="calendar-appointment-status">Status: {selected.status.replaceAll('_',' ')}</p>
        <section className="ecfs-calendar-reservation-notes" aria-label="Reservation notes">
          {appointmentNotes?.important.map(note=><p key={note.noteId}
            className="ecfs-important-customer-note"><strong>! Important customer note:</strong> {note.text}</p>)}
          <h4>Reservation Notes</h4>
          {appointmentNotes?.state==='loading'&&<p role="status">Loading reservation notes…</p>}
          {appointmentNotes?.state==='error'&&
            <p role="alert">Reservation notes are unavailable.</p>}
          {appointmentNotes?.state==='ready'&&<>
            {appointmentNotes.reservation.length===0
              ?<p>No reservation notes yet.</p>
              :<ul className="ecfs-reservation-note-history">
                {appointmentNotes.reservation.map(note=><li key={note.noteId}>
                  <strong>{note.authorName}</strong>
                  <time>{new Intl.DateTimeFormat('en-US',{dateStyle:'short',
                    timeStyle:'short',timeZone}).format(new Date(note.createdAt))}</time>
                  {note.important&&<span className="ecfs-note-important on">!</span>}
                  <p>{note.text}</p>
                  <button type="button" disabled={reservationNoteBusy||!writesEnabled}
                    onClick={()=>void modifyReservationNote(note.noteId,note.version,!note.important)}>
                    {note.important?'Unmark important':'Mark important'}
                  </button>
                  <button type="button" disabled={reservationNoteBusy||!writesEnabled}
                    onClick={()=>{
                      if(window.confirm('Delete this reservation note?'))
                        void modifyReservationNote(note.noteId,note.version);
                    }}>Delete</button>
                </li>)}
              </ul>}
            <label>Add reservation notes
              <textarea rows={2} aria-label="Add reservation notes" maxLength={5000}
                disabled={reservationNoteBusy||!writesEnabled}
                value={reservationNoteText}
                onChange={event=>setReservationNoteText(event.target.value)}/>
            </label>
            <div className="ecfs-profile-note-actions">
              <button type="button" className={'ecfs-note-important '+(reservationNoteImportant?'on':'')}
                disabled={reservationNoteBusy||!writesEnabled}
                aria-label="Mark reservation note important" aria-pressed={reservationNoteImportant}
                onClick={()=>setReservationNoteImportant(value=>!value)}>!</button>
              <button type="button" disabled={reservationNoteBusy||!writesEnabled||!reservationNoteText.trim()}
                onClick={()=>void addReservationNote()}>
                {reservationNoteBusy?'Saving…':'Add Note'}</button>
            </div>
          </>}
          {reservationNoteStatus&&<p role="alert">{reservationNoteStatus}</p>}
        </section>
        <section className="ecfs-calendar-future-discount" aria-label="Future discount code">
          {pendingDiscount&&<p>
            Future discount code: <strong>{pendingDiscount.title}</strong>
            {' ('+pendingDiscount.code+')'}
          </p>}
          <button type="button" disabled={!writesEnabled||futureDiscountBusy}
            onClick={()=>setFutureDiscountSearchOpen(value=>!value)}>
            {pendingDiscount?'Change future discount code':'add a future discount code'}
          </button>
          {pendingDiscount&&<button type="button" disabled={!writesEnabled||futureDiscountBusy}
            onClick={()=>void saveFutureDiscount(null)}>Remove future discount</button>}
          {futureDiscountSearchOpen&&<div className="ecfs-future-discount-picker">
            <label>Search by Common Name or exact code
              <input type="search" aria-label="Search future discount codes"
                value={futureDiscountQuery}
                onChange={event=>setFutureDiscountQuery(event.target.value)}/>
            </label>
            {futureDiscountChoices.length===0&&<p>No enabled codes match.</p>}
            <ul>{futureDiscountChoices.map(choice=><li key={choice.id}>
              <button type="button" title={choice.description}
                disabled={futureDiscountBusy}
                onClick={()=>void saveFutureDiscount({
                  id:String(choice.id),code:choice.code,title:choice.title,
                })}><strong>{choice.title}</strong> ({choice.code})</button>
            </li>)}</ul>
          </div>}
          {futureDiscountBusy&&<p role="status">Saving future discount…</p>}
          {futureDiscountError&&<p role="alert">{futureDiscountError}</p>}
        </section>
        {selected.status==='COMPLETED'&&selected.checkoutSettled!==true&&
          <p className="calendar-disabled-note">Attendance completed; financial checkout not verified.</p>}
        {!advanced?<>
          <button type="button" className="calendar-checkout"
            disabled={!writesEnabled||checkoutBusy||selected.checkoutSettled===true}
            onClick={()=>void onCheckout(selected,false,pendingDiscount).then(result=>{
              if(result==='checked_out'||result==='sale_required')closePopups();
            })}>
            {checkoutBusy?'Checking out…':'Check Out'}
          </button>
          <button type="button" className="calendar-cancel"
            disabled={!writesEnabled||checkoutBusy||selected.status!=='CONFIRMED'}
            onClick={()=>onCancelWithoutCharge(selected)}>Cancel without Charge</button>
          <button type="button" className="calendar-options"
            onClick={()=>setAdvanced(true)}>advanced options</button>
        </>:<>
          <button type="button" className="calendar-reschedule"
            disabled={!writesEnabled||selected.status!=='CONFIRMED'}
            onClick={()=>{onReschedule(selected);closePopups();}}>Reschedule</button>
          <button type="button" className="calendar-cancel-credit"
            disabled={!writesEnabled||selected.status!=='CONFIRMED'||!selected.hasServiceCredit}
            title={!selected.hasServiceCredit?'A verified service credit is required':undefined}
            onClick={()=>onCancelWithCredit(selected)}>
            Cancel and Use Service Credit
          </button>
          <button type="button" className="calendar-checkout-new-sale"
            disabled={!writesEnabled||checkoutBusy||selected.checkoutSettled===true}
            onClick={()=>void onCheckout(selected,true,pendingDiscount).then(result=>{
              if(result==='checked_out'||result==='sale_required')closePopups();
            })}>Check Out with New Sale</button>
          <button type="button" className="calendar-checkout-other"
            disabled={!writesEnabled||checkoutBusy||selected.checkoutSettled===true}
            onClick={()=>onCheckoutOtherCustomer(selected,pendingDiscount)}>Checkout Paid by Other Customer</button>
          <button type="button" className="calendar-options"
            onClick={()=>setAdvanced(false)}>basic options</button>
        </>}
        {writesEnabled&&checkInEnabled&&selected.status==='CONFIRMED'&&
          <div className="calendar-quick-actions">
            <button type="button" onClick={()=>{onCheckIn(selected);setSelectedId(null);}}>
              Check in guest</button>
          </div>}
        {writesEnabled&&checkInEnabled&&selected.status==='CHECKED_IN'&&
          <button type="button" onClick={()=>{onComplete(selected);setSelectedId(null);}}>
            Complete appointment</button>}
        {!writesEnabled&&<p className="calendar-disabled-note">
          Appointment changes are disabled in this staging environment.
        </p>}
      </div>
    </aside>}
    {hovered&&<aside className="calendar-popover calendar-customer-popup"
      style={customerPlacement??undefined} role="dialog"
      aria-label={'Customer preview for '+hovered.customerName}>
      <div className="calendar-popover-heading"><strong>{hovered.customerName}</strong>
        <button type="button" onClick={closePopups} aria-label="Close customer preview">×</button>
      </div>
      {preview?.state==='loading'&&<p role="status">Loading authorized customer details…</p>}
      {preview?.state==='denied'&&<p role="status">Contact details require customer profile access.</p>}
      {preview?.state==='error'&&<p role="status">Customer details are unavailable.</p>}
      {preview?.state==='ready'&&<div className="calendar-popover-content">
        <p>{preview.profile?.email??'No email recorded'}</p>
        <p>{preview.profile?.phoneE164??'No phone recorded'}</p>
        <h3>Recent appointment history</h3>
        {preview.visits
          ?<><p>{preview.visits.hasMore?'More than ':' '}
            {preview.visits.appointments.length} appointments in available history</p>
          {preview.visits.appointments.slice(0,3).map((visit,index)=>
            <p key={index}>{visit.serviceName} · {visit.locationName} · {timeLabel(visit.startsAt,visit.locationTimezone??timeZone)}</p>)}</>
          :<p>Visit history requires separate permission.</p>}
        <p className="calendar-disabled-note">
          Credit balances, payment cards, and lifetime spend will appear after POS integration.
        </p>
        {onOpenCustomer&&<button type="button" className="calendar-open-customer"
          onClick={()=>{onOpenCustomer(hovered);closePopups();}}>
          Open customer profile
        </button>}
      </div>}
    </aside>}
  </section>;
}
